import { inject, untracked } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { PermissionService, StaffFeature } from '../services/permission.service';

export const authGuard = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    const user = authService.currentUser();
    const isStaff = authService.isStaff();

    if (user && !user.profile.email_verified && !isStaff) {
      const isNoticePage = state.url.includes('/auth/verify-email-notice');
      if (!isNoticePage) {
        return router.createUrlTree(['/auth/verify-email-notice']);
      }
    }
    return true;
  }

  if (state.url === "/auth/login") {
    return true;
  }

  // Preserve the intended target so login can return the user here
  // (e.g. a check-in invitation link opened while logged out).
  return router.createUrlTree(["/auth/login"], { queryParams: { returnUrl: state.url } });
};

export const guestGuard = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return true;
  }

  if (authService.isStaff()) {
    if (state.url.includes('/staff/dashboard')) return true;
    return router.createUrlTree(['/staff/dashboard']);
  } else {
    if (state.url.includes('/events') && !state.url.includes('/auth')) return true;
    return router.createUrlTree(['/events']);
  }
};

export const staffGuard = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    if (!authService.isStaff()) {
      return router.createUrlTree(['/events']);
    }
    return true;
  }

  // Preserve the intended target so login can return the user here
  // (e.g. a QR scan that hit a protected route while logged out).
  return router.createUrlTree(["/auth/login"], { queryParams: { returnUrl: state.url } });
};

/**
 * Role-aware landing redirect for the staff app.
 *
 * Access-only users (in group "Access" but not "Registration", non-superuser)
 * dead-end on the Dashboard, which featureGuard blocks for them. Send them
 * straight to the participant scanner kiosk instead. Everyone else lands on
 * the dashboard as before.
 */
export const staffHomeRedirect = () => {
  const router = inject(Router);
  const permissionService = inject(PermissionService);
  const target = untracked(() =>
    permissionService.isAccessOnly() ? '/staff/runner-scanner' : '/staff/dashboard'
  );
  return router.createUrlTree([target]);
};

export function featureGuard(feature: StaffFeature) {
  return () => {
    const permissionService = inject(PermissionService);
    const result = untracked(() => permissionService.canAccess(feature)());
    if (result) return true;
    // Return false to block navigation — the sidebar only shows accessible links,
    // so users shouldn't normally hit this unless typing URLs manually
    return false;
  };
}
